import React, { useMemo, useState } from "react";
import { TablePage } from "@outpave/ds";
import { useFeatureEntitlements } from "@frontegg/react";
import dayjs from "dayjs";
import AccountType from "enums/AccountType";
import Role from "enums/Role";
import { TimeFrameEnum } from "enums/AnalyticsTimeFrame";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import useGetAccounts from "hooks/useGetAccounts";
import useGetBankAccounts from "hooks/useGetBankAccounts";
import useCashFlowSummary from "hooks/useCashFlowSummary";
import useProgramInsights from "hooks/useProgramInsights";
import useSpendingData from "hooks/useSpendingData";
import {
  useCardRequests,
  useOutstandingInvoices,
  useOutstandingPayments,
  useReimbursementsDue,
  useRewardsBalance,
} from "hooks/useDashboardQueues";
import { Columns, Stack } from "components/layout/reusableLayouts";
import BirthdayBanner from "components/common/BirthdayBanner";
import ExternalAccountDisconnectedPopover from "components/admin/ExternalAccountDisconnectedPopover";
import AccountsPanel from "components/admin/dashboard/AccountsPanel";
import BalancePanel from "components/admin/dashboard/BalancePanel";
import ProgramInsightsPanel from "components/common/ProgramInsightsPanel";
import QueueCards from "components/admin/dashboard/QueueCards";
import { showsBalancePanel } from "utils/accounts/showsBalancePanel";
import SpendComparisonPanel from "components/admin/dashboard/SpendComparisonPanel";
import TopCategoriesPanel from "components/common/TopCategoriesPanel";

/**
 * How long the disconnected-account popover stays quiet after being SHOWN.
 * `handleDisconnected` stamps `lastPopupShown` as it opens the popover, not as
 * the user dismisses it, so the window runs from the appearance.
 */
const DISCONNECTED_POPOVER_QUIET_DAYS = 1;

/**
 * The band the balance and accounts row lives in, px. The ceiling keeps the
 * panels from pushing the analysis row off the fold; the floor keeps the
 * transactions list from collapsing to a couple of rows.
 *
 * Between them the row takes whatever the other two rows leave, which is how
 * the prototype does it (`flex-1 min-h-0 max-h-128`). It used to compute the
 * space instead, as `clamp(360px, calc(100vh - 208px), 512px)`, and that calc
 * resolved past the ceiling on any normal window: the row pinned at 512 and
 * ignored the page, while the prototype's sat at 360 on the same viewport. A
 * measured leftover beats a subtracted guess, and it takes the guess (208px of
 * assumed chrome) out of the file.
 */
const PANEL_ROW_MIN_HEIGHT = 360;
const PANEL_ROW_MAX_HEIGHT = 512;

const Dashboard: React.FC = () => {
  const [showDisconnected, setShowDisconnected] = useState<boolean>(false);

  const { isEntitled: featureRewards } =
    useFeatureEntitlements("feature_rewards");
  const { isEntitled: featureInvoices } =
    useFeatureEntitlements("feature_invoices");
  const { isEntitled: featurePayments } =
    useFeatureEntitlements("feature_payments");

  const isOwner = useCheckUserRole(Role.Owner, { exact: true });
  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });
  const isAccountant = useCheckUserRole(Role.Accountant, { exact: true });
  const isAuditorOrAccountant = useMemo(
    () => isAccountant || isAuditor,
    [isAccountant, isAuditor],
  );

  // Fetch bank accounts once for the whole dashboard (superset of what the
  // balance and accounts panels need) and pass it down, instead of each child
  // firing its own overlapping getListBankAccounts query.
  const bankAccounts = useGetBankAccounts({
    accountTypes: [
      AccountType.FINANCIAL_ACCOUNT,
      AccountType.PLAID_EXTERNAL_CARD_ACCOUNT,
      AccountType.BANK,
    ],
  });
  const {
    internalAccounts,
    combinedBalanceAmount,
    isLoading: loadingAccounts,
    loadingBalances,
  } = bankAccounts;

  const { externalAccounts, loading: loadingExternalAccounts } = useGetAccounts(
    {
      onCompleted: (data) => {
        if (data?.hasDisconnectedAccount) handleDisconnected();
      },
    },
  );

  const showBalance = showsBalancePanel({
    settled: !loadingAccounts && !loadingExternalAccounts,
    internalCount: internalAccounts.length,
    externalCount: externalAccounts.length,
  });

  const handleDisconnected = () => {
    const lastPopupShown = localStorage.getItem("lastPopupShown");
    const now = dayjs();

    // No timestamp, or one older than the quiet window, means show it again.
    if (
      !lastPopupShown ||
      dayjs(lastPopupShown).isBefore(
        now.subtract(DISCONNECTED_POPOVER_QUIET_DAYS, "day"),
      )
    ) {
      localStorage.setItem("lastPopupShown", now.toISOString());
      setShowDisconnected(true);
    }
  };

  const showRewards = isOwner && featureRewards;

  // The page owns every queue fetch: the tiles show them and the insights rank
  // them, and two owners of one query is two networks calls for one number.
  const cardRequests = useCardRequests();
  const payments = useOutstandingPayments({ skip: !featurePayments });
  const invoices = useOutstandingInvoices({ skip: !featureInvoices });
  const reimbursements = useReimbursementsDue();

  const rewards = useRewardsBalance({ skip: !showRewards });

  const cashFlow = useCashFlowSummary(TimeFrameEnum.LAST_30_DAYS);

  const topCategories = useSpendingData({
    type: "pie",
    groupKey: "expenseCategory",
    filters: { timeFrame: TimeFrameEnum.LAST_30_DAYS },
  });

  const { insights, loading: loadingInsights } = useProgramInsights({
    payments,
    invoices,
    reimbursements,
    balance: combinedBalanceAmount,
    outflow: cashFlow.outflow,
    currency: cashFlow.currency,
  });

  return (
    <TablePage wrapped={false}>
      <BirthdayBanner />

      <Stack gap="var(--space-4)" sx={{ flex: 1, minHeight: 0 }}>
        <QueueCards
          cardRequests={cardRequests}
          payments={payments}
          invoices={invoices}
          reimbursements={reimbursements}
          rewards={rewards}
          currency={cashFlow.currency}
          showPayments={featurePayments}
          showInvoices={featureInvoices}
          showRewards={showRewards}
        />

        <Columns
          gap="var(--space-4)"
          count={{
            xs: 1,
            lg: showBalance && !isAuditorOrAccountant ? "7fr 5fr" : 1,
          }}
          // The height band is for the side-by-side row: stacked, it squeezed both panels into one row's height.
          sx={{
            flex: { lg: 1 },
            minHeight: { lg: PANEL_ROW_MIN_HEIGHT },
            maxHeight: { lg: PANEL_ROW_MAX_HEIGHT },
          }}
        >
          {showBalance && (
            <BalancePanel
              balance={combinedBalanceAmount}
              balanceLoading={loadingAccounts || loadingBalances}
              cashFlow={cashFlow}
            />
          )}
          {!isAuditorOrAccountant && (
            <AccountsPanel bankAccounts={bankAccounts} />
          )}
        </Columns>

        <Columns gap="var(--space-4)" count={{ xs: 1, md: 2, xl: 3 }}>
          <TopCategoriesPanel
            categories={topCategories.pieData.map((entry) => ({
              id: entry.name,
              label: entry.name,
              value: entry.value,
            }))}
            currency={topCategories.currency}
            loading={topCategories.loading}
          />
          <ProgramInsightsPanel insights={insights} loading={loadingInsights} />
          <SpendComparisonPanel />
        </Columns>
      </Stack>

      <ExternalAccountDisconnectedPopover
        open={showDisconnected}
        handleClose={() => setShowDisconnected(false)}
      />
    </TablePage>
  );
};

export default Dashboard;
